/**
    * Preferences management mixin
    * Provides functionality for managing user preferences in the Mikomic editor
    * including interface settings, editor options, and autosave functionality.
 */
export default {
    data() {
        return {
            // Preferences
            show_preferences_popup: false,
            active_preferences_category: 'interface',
            preferences_categories: [
                { id: 'interface' },
                { id: 'editor' },
                { id: 'chapters' }
            ],
            preferences: {
                interface: {
                    language: 'en',
                    scale: '100',
                    language_initialized: false,
                },
                editor: {
                    autosave_enabled: true,
                    autosave_interval: 20,
                    history_steps: 20,
                    show_guides: true,
                    transformer_keep_ratio: true,
                    insert_to_global_panel: false
                },
                chapters: {
                    open_last_page: false,
                    show_pages_navigation_by_default: false
                },
                layer_defaults: {
                    blend_mode: 'normal',
                    opacity: 1,
                    visible: true,
                    locked: false,
                },
                balloon_defaults: {
                    fill: '#FFFFFF',
                    stroke: '#262626',
                    stroke_width: 2,
                    stroke_dash: '',
                    alpha: 1,

                },
                text_defaults: {
                    font_size: 22,
                    font_family: 'New Wild Words, Arial, sans-serif',
                    font_style: 'normal',
                    text_color: '#1c1c1c',
                    text_stroke: '#FFFFFF',
                    text_stroke_width: 0,
                    line_height: 1,
                    align: 'center',
                    is_bold: false,
                    is_italic: false
                },
                ui: {
                    panel_collapsed: {
                        object_props: false,
                        page_props: false,
                        balloon_panel: false,
                        layers: false
                    }
                }
            },
            autosave_timer: null,
        };
    },

    mounted() {
        // Don't load preferences automatically - wait for user state to be determined
        // Preferences will be loaded by check_php_session or login
    },
    
    methods: {

        //! Preferences
        async load_preferences() {
            // Load preferences from database if user is logged in
            if (this.user_data && this.user_data.email) {
                try {
                    const form_data = new FormData();
                    form_data.append('request', 'load_user_preferences');
                    
                    const response = await axios.post('ajax.php', form_data, {
                        headers: {'Content-Type': 'multipart/form-data'}
                    });
                    
                    if (response.data.status === 'success' && response.data.preferences) {
                        // Create a deep copy of default preferences
                        const default_preferences = JSON.parse(JSON.stringify(this.preferences));
                        // Merge saved preferences on top of defaults
                        this.preferences = this.deep_merge(default_preferences, response.data.preferences);
                    }
                } catch (error) {
                    console.error("Error loading preferences from database:", error);
                }
            }
            
            // When loading preferences, respect the server-detected language
            if (this.current_language && !this.preferences.interface.language_initialized) {
                this.preferences.interface.language = this.current_language;
                this.preferences.interface.language_initialized = true;
            }
            
            // Apply preferences after loading them
            this.apply_preferences();
            
            // Initialize text editing from defaults after preferences are loaded
            if (typeof this.initialize_text_editing_from_defaults === 'function') {
                this.initialize_text_editing_from_defaults();
            }

            this.initialize_balloon_defaults();
        },
        
        load_preferences_from_response(preferences_data) {
            // Method to load preferences from login/session response
            if (preferences_data) {
                try {
                    // Create a deep copy of default preferences
                    const default_preferences = JSON.parse(JSON.stringify(this.preferences));
                    // Merge saved preferences on top of defaults
                    this.preferences = this.deep_merge(default_preferences, preferences_data);
                } catch (error) {
                    console.error("Error loading preferences from response:", error);
                }
            }
            
            // When loading preferences, respect the server-detected language
            if (this.current_language && !this.preferences.interface.language_initialized) {
                this.preferences.interface.language = this.current_language;
                this.preferences.interface.language_initialized = true;
            }
            
            // Apply preferences after loading them
            this.apply_preferences();
            
            // Initialize text editing from defaults after preferences are loaded
            if (typeof this.initialize_text_editing_from_defaults === 'function') {
                this.initialize_text_editing_from_defaults();
            }

            this.initialize_balloon_defaults();
        },
        
        deep_merge(target, source) {
            const output = Object.assign({}, target);
            
            if (this.is_object(target) && this.is_object(source)) {
                Object.keys(source).forEach(key => {
                    if (this.is_object(source[key])) {
                        if (!(key in target)) {
                            Object.assign(output, { [key]: source[key] });
                        } else {
                            output[key] = this.deep_merge(target[key], source[key]);
                        }
                    } else {
                        Object.assign(output, { [key]: source[key] });
                    }
                });
            }
            
            return output;
        },
        
        is_object(item) {
            return (item && typeof item === 'object' && !Array.isArray(item));
        },
        
        async save_preferences(silent_ui_exit = false) {
            // Check if user is logged in
            if (!this.user_data || !this.user_data.email) {
                // For UI preferences (like panel collapse), exit silently without saving
                if (silent_ui_exit) {
                    return;
                }
                // Show registration modal for other preferences
                this.show_premium_modal('registered_only', this.get_t('auth.registration_required_to_save_preferences'));
                return;
            }
            
            // Check premium limits for editor preferences
            const history_steps = parseInt(this.preferences.editor.history_steps) || 20;
            if (history_steps > 20) {
                if (!this.check_local_restriction('max_undo_steps', history_steps)) {
                    return;
                }
            }

            const autosave_interval = parseInt(this.preferences.editor.autosave_interval) || 20;
            if (autosave_interval < 20) {
                if (!this.check_local_restriction('min_autosave_interval', autosave_interval)) {
                    return;
                }
            }

            // Save preferences to database
            try {
                const form_data = new FormData();
                form_data.append('request', 'save_user_preferences');
                form_data.append('preferences', JSON.stringify(this.preferences));
                
                const response = await axios.post('ajax.php', form_data, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                
                if (response.data.status !== 'success') {
                    console.error("Error saving preferences to database:", response.data.message);
                }
            } catch (error) {
                console.error("Error saving preferences to database:", error);
            }
            
            // Apply preferences to the app
            this.apply_preferences();
            
            // Close the popup
            this.close_preferences_popup();
        },
        
        apply_preferences() {
            // Apply language change if needed and different from current
            const new_language = this.preferences.interface.language;
            if (typeof this.load_language_from_preferences === 'function' && 
                new_language && new_language !== this.current_language) {
                this.load_language_from_preferences();
            }
            
            // Apply interface scale
            this.apply_interface_scale(this.preferences.interface.scale);
            
            // Apply autosave settings
            this.setup_autosave();
            
            // Apply history steps setting
            this.set_history_length();
            
            // Sync gallery insert preference
            if (this.insert_to_global_panel !== undefined) {
                this.insert_to_global_panel = this.preferences.editor.insert_to_global_panel;
            }
            
            // Apply pages navigation visibility preference
            if (this.show_pages_navigation !== undefined) {
                this.show_pages_navigation = this.preferences.chapters.show_pages_navigation_by_default;
                
                // Force layout recalculation when visibility changes (same as toggle function)
                this.$nextTick(() => {
                    setTimeout(() => {
                        if (typeof this.handle_window_resize === 'function') {
                            this.handle_window_resize();
                        }
                    }, 1);
                });
            }
        },
        
        set_history_length() {
            this.max_history_length = parseInt(this.preferences.editor.history_steps) || 20;
        },

        apply_interface_scale(scale) {
            // Apply scale to the document root for better responsiveness
            document.documentElement.style.fontSize = `${scale}%`;
            
            // Optionally adjust UI elements that might need specific scaling 
            this.$nextTick(() => {
                // Redraw canvas to ensure proper sizing
                if (this.stage) {
                    this.stage.batchDraw();
                }
            });
        },
        
        setup_autosave() {
            // Clear existing autosave timer
            if (this.autosave_interval) {
                clearInterval(this.autosave_interval);
                this.autosave_interval = null;
            }
            
            // Start the autosave timer using the document mixin method
            // This will read directly from preferences
            this.start_autosave_timer();
            
            const is_enabled = this.preferences.editor.autosave_enabled;
            const interval = this.preferences.editor.autosave_interval;
            
            //console.log(`Autosave ${is_enabled ? 'enabled: every ' + interval + ' minutes' : 'disabled'}`);
        },
        

    }
};